export function parseSorceryImportText(text) {
  const lines = String(text || '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  let avatar = '';
  let currentZone = '';
  const cards = [];

  const cardLineRegex = /^\s*(\d{1,3})\s*x\s+(.+?)\s*$/i;
  const sectionHeaderRegex = /^(spellbook|atlas|collection)\s*:?\s*$/i;
  const avatarRegex = /^avatar\s*:\s*(.+)$/i;

  for (const line of lines) {
    const avatarMatch = avatarRegex.exec(line);
    if (avatarMatch) {
      avatar = avatarMatch[1].trim();
      continue;
    }

    const sectionMatch = sectionHeaderRegex.exec(line);
    if (sectionMatch) {
      currentZone = sectionMatch[1].toLowerCase();
      continue;
    }

    const cardMatch = cardLineRegex.exec(line);
    if (cardMatch) {
      cards.push({
        count: parseInt(cardMatch[1], 10),
        name: cardMatch[2].trim(),
        zone: currentZone,
      });
    }
  }

  return { avatar, cards };
}

// Curiosa lives on sorcerytcg.com since August 2026; curiosa.io links
// still circulate (and redirect there with the deck id intact), so both
// hosts are accepted.
export function isStandaloneCuriosaDeckUrl(text) {
  return /^https?:\/\/(?:www\.)?(?:curiosa\.io|sorcerytcg\.com)\/decks\/[a-z0-9]+\/?$/i.test(String(text || '').trim());
}

export function extractCuriosaDeckId(url) {
  const match = String(url || '').match(/(?:curiosa\.io|sorcerytcg\.com)\/decks\/([a-z0-9]+)/i);
  return match ? match[1] : null;
}

/**
 * Resolve parsed deck entries (`{ avatar, cards: [{ name, count, zone }] }`)
 * against the normalized card catalog. Shared by both import paths: pasted
 * text via parseSorceryImportText, and Curiosa URLs via the
 * /api/curiosa/deck proxy, which returns entries in the same shape.
 *
 * Entries without a zone fall back to the card's type, so a Curiosa main
 * deck splits into atlas / spellbook the same way a pasted list does.
 */
export function resolveSorceryDeckEntries({ avatar, cards } = {}, sorceryCards = []) {
  const cardsByName = new Map();
  for (const card of sorceryCards) {
    cardsByName.set(card.name?.toLowerCase(), card);
  }

  const resolved = [];
  const warnings = [];
  const push = (card, zone) => {
    resolved.push({
      cardId: card.unique_id,
      cardName: card.name,
      printingId: card.printings?.[0]?.unique_id || card.unique_id,
      isSideboard: zone === 'collection',
      zone,
    });
  };

  if (avatar) {
    const card = cardsByName.get(avatar.toLowerCase());
    if (card) push(card, 'avatar');
    else warnings.push(`Avatar not found: ${avatar}`);
  }

  for (const entry of cards || []) {
    const card = cardsByName.get(entry.name?.toLowerCase());
    if (!card) {
      warnings.push(`Card not found: ${entry.name}`);
      continue;
    }
    const zone = entry.zone || (card.type === 'Site' ? 'atlas' : 'spellbook');
    for (let i = 0; i < (entry.count || 1); i++) push(card, zone);
  }

  return { cards: resolved, warnings };
}
